import { defineStore } from "pinia";
import { ref, watch } from "vue";
import { i18n, SUPPORTED_LOCALES, type SupportedLocale } from "@/i18n";

/**
 * i18n store — keeps the user's preferred locale in localStorage and
 * mirrors it onto the global `vue-i18n` instance.
 *
 * The actual translation pipeline lives in `@/i18n` (createI18n).
 * This store is the user-facing surface: read `locale`, call
 * `setLocale('fi' | 'en')`, and the rest of the app re-renders.
 *
 * The default locale is Finnish — this is a Finnish-first product;
 * English exists as a stub today and falls back to Finnish for
 * any key that hasn't been translated yet (see `fallbackLocale`
 * in `@/i18n`).
 */
const STORAGE_KEY = "web1-crm:locale";

function readPersisted(): SupportedLocale {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && (SUPPORTED_LOCALES as readonly string[]).includes(v)) {
      return v as SupportedLocale;
    }
  } catch {
    /* SSR / privacy mode — fall through */
  }
  return "fi";
}

export const useI18nStore = defineStore("i18n", () => {
  const locale = ref<SupportedLocale>(readPersisted());

  // Apply the persisted locale to the global vue-i18n instance on
  // first construction so any component that reads `t()` synchronously
  // already sees the right language.
  i18n.global.locale.value = locale.value;

  watch(locale, (next) => {
    i18n.global.locale.value = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  });

  function setLocale(next: SupportedLocale): void {
    locale.value = next;
  }

  return { locale, setLocale };
});
